import { useCallback, useEffect, useRef, useState } from "react"

const MAX_RECORDING_MS = 60_000

/**
 * Hook para gravar áudio do microfone com MediaRecorder.
 *
 * Provê amplitudes em tempo real (4 bins via AnalyserNode) pra waveform.
 * Auto-stop em 60s. Limpa recursos automaticamente em unmount + beforeunload.
 */
export interface UseVoiceRecordingReturn {
  start: () => Promise<void>
  stop: () => Promise<{ blob: Blob; durationMs: number } | null>
  cancel: () => void
  isRecording: boolean
  durationMs: number
  amplitudes: number[]
  permissionError: string | null
  isSupported: boolean
}

function pickMimeType(): string {
  const MR = (typeof window !== "undefined" ? window.MediaRecorder : undefined) as
    | typeof MediaRecorder
    | undefined
  if (!MR) return ""
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ]
  for (const c of candidates) {
    if (MR.isTypeSupported(c)) return c
  }
  return ""
}

export function useVoiceRecording(): UseVoiceRecordingReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [durationMs, setDurationMs] = useState(0)
  const [amplitudes, setAmplitudes] = useState<number[]>([0, 0, 0, 0])
  const [permissionError, setPermissionError] = useState<string | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startTimeRef = useRef<number>(0)
  const tickRef = useRef<number | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const stopResolverRef = useRef<((value: { blob: Blob; durationMs: number } | null) => void) | null>(null)
  const cancelledRef = useRef(false)

  const isSupported =
    typeof window !== "undefined" &&
    "MediaRecorder" in window &&
    !!navigator.mediaDevices?.getUserMedia

  const cleanupAll = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current)
      tickRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop()
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
      analyserRef.current = null
    }
    recorderRef.current = null
    chunksRef.current = []
    setIsRecording(false)
    setDurationMs(0)
    setAmplitudes([0, 0, 0, 0])
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) {
      setPermissionError("Seu navegador não suporta gravação de áudio.")
      return
    }
    if (isRecording) return

    setPermissionError(null)
    cancelledRef.current = false

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      console.error("getUserMedia failed:", err)
      setPermissionError(
        "Precisamos da permissão do microfone. Verifique as configurações do navegador.",
      )
      return
    }

    streamRef.current = stream
    chunksRef.current = []

    const mimeType = pickMimeType()
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    recorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const resolver = stopResolverRef.current
      stopResolverRef.current = null

      if (cancelledRef.current) {
        cleanupAll()
        resolver?.(null)
        return
      }

      const finalBlob = new Blob(chunksRef.current, {
        type: recorder.mimeType || mimeType || "audio/webm",
      })
      const finalDuration = Date.now() - startTimeRef.current
      cleanupAll()
      resolver?.({ blob: finalBlob, durationMs: finalDuration })
    }

    // AnalyserNode pra amplitudes (waveform)
    try {
      const AudioCtxCtor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new AudioCtxCtor()
      audioCtxRef.current = ctx
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser
    } catch (err) {
      console.warn("AudioContext failed:", err)
    }

    startTimeRef.current = Date.now()
    recorder.start(250) // chunk every 250ms
    setIsRecording(true)

    // Tick pra atualizar duração + amplitudes
    tickRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      setDurationMs(elapsed)

      if (analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount)
        analyserRef.current.getByteFrequencyData(data)
        // Bin os valores em 4 grupos pra waveform compacto
        const bins = [0, 0, 0, 0]
        const binSize = Math.floor(data.length / 4)
        for (let g = 0; g < 4; g++) {
          let sum = 0
          for (let i = 0; i < binSize; i++) {
            sum += data[g * binSize + i] ?? 0
          }
          bins[g] = sum / binSize / 255 // normalize 0..1
        }
        setAmplitudes(bins)
      }

      if (elapsed >= MAX_RECORDING_MS) {
        // Auto-stop ao bater limite
        recorderRef.current?.stop()
      }
    }, 100)
  }, [isRecording, isSupported, cleanupAll])

  const stop = useCallback((): Promise<{ blob: Blob; durationMs: number } | null> => {
    return new Promise((resolve) => {
      if (!recorderRef.current || recorderRef.current.state === "inactive") {
        cleanupAll()
        resolve(null)
        return
      }
      stopResolverRef.current = resolve
      recorderRef.current.stop()
    })
  }, [cleanupAll])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop()
    } else {
      cleanupAll()
    }
  }, [cleanupAll])

  // Limpa em unmount + beforeunload
  useEffect(() => {
    const onBeforeUnload = () => cancel()
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload)
      cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    start,
    stop,
    cancel,
    isRecording,
    durationMs,
    amplitudes,
    permissionError,
    isSupported,
  }
}
